import { pipe, Runtime } from "fp";

import React from "react";

import { Company } from "~/Company";
import * as Context from "~/Context";
import * as Data from "~/Data";
import { Component as LinkComponent } from "~/Link";
import { Remote } from "~/Remote";
import { Time } from "~/Time";
import * as TimeTracking from "~/TimeTracking";

import * as Request from "./Request";

export * as Action from "./Action";
export * as Request from "./Request";

export type WorkCode = Data.Reference.Reference & {
  readonly customID?: Data.ID.ID;
  readonly name: string;
};

export const toDescription = ({ id, customID, name }: WorkCode): string =>
  `${customID ?? id} – ${name}`;

export const useStore = (): Data.Store.Store<WorkCode> =>
  Data.Store.useSelector(({ workCodes: { store } }) => store);

export const useDescriptions = (
  entry?: TimeTracking.Entry.Entry,
  workCode?: Data.Reference.Reference
): string => {
  const store = useStore();
  return pipe(store, asDescription(entry, workCode));
};

export const asDescription =
  (entry?: TimeTracking.Entry.Entry, workCode?: Data.Reference.Reference) =>
  (store: Data.Store.Store<WorkCode>): string => {
    if (workCode) {
      return pipe(
        store,
        Data.Store.Get.fromReference({
          ...workCode,
          missing: () => workCode.id,
          found: toDescription,
        })
      );
    }
    if (entry && entry.workCodes) {
      return entry.workCodes.map(toDescription).join(", ");
    }
    return "";
  };

export const useFromJob = (
  job?: Data.Reference.Reference
): Remote<readonly WorkCode[]> => {
  const { company } = Company.use();
  return pipe(
    Remote.Query.use<readonly WorkCode[]>(
      Request.Query.key({ company, job }),
      Request.Query.request({ company, job }),
      {
        keepPreviousData: true,
        staleTime: Time.Duration.minutes(5).duration,
      }
    )
  );
};

export namespace State {
  export type State = { readonly store: Data.Store.Store<WorkCode> };

  export const initial = (): State => ({ store: Data.Store.initial() });
}

export namespace Response {
  export const runtime = () =>
    Runtime.strict({
      work_code_id: Runtime.number(),
      custom_id: Runtime.string(),
      name: Runtime.string(),
    });

  export const decode = (
    response: Runtime.ToType<typeof runtime>
  ): WorkCode => ({
    id: `${response.work_code_id}`,
    customID: response.custom_id ? `${response.custom_id}` : undefined,
    name: response.name,
  });
}

export namespace Link {
  type Props = React.HTML<"a"> & {
    readonly entry?: TimeTracking.Entry.Entry;
    readonly workCode?: Data.Reference.Reference;
  };

  export const useURL = (): string =>
    `${Context.useBaseURL()}/#/home/company-admin/work`;

  export const Component: React.Fn<Props> = ({ entry, workCode, ...props }) => (
    <LinkComponent {...props} href={useURL()}>
      {useDescriptions(entry, workCode)}
    </LinkComponent>
  );
}
